import { useOrgContext } from '@/org/contexts/OrgContext'
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  IconButton,
  SimpleGrid,
  Spacer,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import {
  NextStepFragment,
  useArchiveDoneNextStepsMutation,
  useArchiveNextStepMutation,
  useCircleNextStepsSubscription,
  useUpdateNextStepMutation,
} from '@gql'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArchiveIcon, CreateIcon } from 'src/icons'
import NextStepModal from '../modals/NextStepModal'
import NextStepItem from './NextStepItem'
import { useMeetingNextSteps } from '@/meeting/contexts/MeetingNextStepsContext'

// Ten per column, expandable on click (D23). A meeting reads its balance at a
// glance; a circle with forty open steps has a problem the screen should show,
// not hide behind a scrollbar.
const COLUMN_LIMIT = 10

interface Props {
  circleId: string
  // A finished meeting still shows its board — reading what was decided is the
  // main use of a past report — but offers no action (G2-D17).
  readOnly?: boolean
  onAdd?(): void
}

export default function NextStepsBoard({
  circleId,
  readOnly,
  onAdd,
}: Props) {
  const { t } = useTranslation()
  const { orgData } = useOrgContext()
  const [updateNextStep] = useUpdateNextStepMutation()
  const [archiveNextStep] = useArchiveNextStepMutation()
  const [archiveDoneNextSteps] = useArchiveDoneNextStepsMutation()
  const [expanded, setExpanded] = useState<{ todo: boolean; done: boolean }>({
    todo: false,
    done: false,
  })
  // The step being corrected (C2). Editing is not resolving: one mistypes,
  // especially when writing out loud during a meeting.
  const [editing, setEditing] = useState<NextStepFragment | undefined>()

  // The board's scope is the circle's own — see OrgData.circleWithChildren for
  // why it stops at direct children. With a plain _eq on the meeting's circle,
  // a step handed to a sub-circle would vanish from the very meeting that just
  // created it (the REUN-02 failure, 04/08/2026).
  const circlesIds = useMemo(
    () => (orgData?.circleWithChildren(circleId) ?? []).map((c) => c.id),
    [orgData, circleId]
  )

  // Inside a meeting, the list is shared with the tensions steps (#42)
  const meetingNextSteps = useMeetingNextSteps()
  const { data } = useCircleNextStepsSubscription({
    skip: !!meetingNextSteps,
    variables: { circlesIds },
  })
  const nextSteps = meetingNextSteps
    ? meetingNextSteps.nextSteps
    : data?.next_step

  const [todo, done] = useMemo(() => {
    const list = nextSteps ?? []
    return [list.filter((s) => !s.done), list.filter((s) => s.done)]
  }, [nextSteps])

  const handleToggle = useCallback(
    (nextStep: NextStepFragment) =>
      updateNextStep({
        variables: { id: nextStep.id, values: { done: !nextStep.done } },
      }),
    [updateNextStep]
  )

  const handleArchive = useCallback(
    (nextStep: NextStepFragment) =>
      archiveNextStep({
        variables: { id: nextStep.id, archivedAt: new Date().toISOString() },
      }),
    [archiveNextStep]
  )

  // Only the ticked ones (G1-D11). The rule also lives in the mutation itself,
  // so even a wrong call cannot file away an unkept commitment.
  const handleArchiveDone = useCallback(
    () =>
      archiveDoneNextSteps({
        variables: { circlesIds, archivedAt: new Date().toISOString() },
      }),
    [archiveDoneNextSteps, circlesIds]
  )

  const renderColumn = (
    key: 'todo' | 'done',
    steps: readonly NextStepFragment[]
  ) => {
    const isExpanded = expanded[key]
    const visible = isExpanded ? steps : steps.slice(0, COLUMN_LIMIT)
    const hidden = steps.length - visible.length

    return (
      <Box>
        <Flex align="baseline" pb={2} mb={3} borderBottomWidth="1px">
          <Heading as="h4" size="xs" textTransform="uppercase" color="gray.500">
            {t(`NextStepsBoard.${key}`)}
          </Heading>
          <Spacer />
          <Text fontSize="sm" color="gray.500">
            {steps.length}
          </Text>
        </Flex>

        <Flex direction="column">
          {visible.map((nextStep) => (
            // Rows touch and light up on hover (ENR06-D5, D8). The column is in
            // half a width here — this board is a two-column grid — so every
            // pixel spent on anything but the title is taken from the title.
            <Flex
              key={nextStep.id}
              align="center"
              gap={2}
              p={1}
              borderRadius="md"
              _hover={{ bg: 'bgItemHover' }}
            >
              <Checkbox
                isChecked={nextStep.done}
                isDisabled={readOnly}
                onChange={() => handleToggle(nextStep)}
              />
              <NextStepItem
                nextStep={nextStep}
                onEdit={readOnly ? undefined : () => setEditing(nextStep)}
                flex={1}
                minW={0}
              />
              {/* Only on a step already done (Yohan, 11/08/2026): an
                  "Archive" button facing every step still owed added a way out
                  where the board should be asking for a commitment. Filing away
                  what is not done goes through the global button, deliberately
                  one gesture further.
                  Icon only since ENR06-D6: the labelled button ate a third of a
                  half-width column, and it took that third from the title. */}
              {!readOnly && nextStep.done && (
                <Tooltip label={t('NextStepsBoard.archiveOne')}>
                  <IconButton
                    size="xs"
                    variant="ghost"
                    flexShrink={0}
                    icon={<ArchiveIcon size={16} />}
                    aria-label={t('NextStepsBoard.archiveOne')}
                    onClick={() => handleArchive(nextStep)}
                  />
                </Tooltip>
              )}
            </Flex>
          ))}

          {steps.length === 0 && (
            <Text fontSize="sm" color="gray.500">
              {t(`NextStepsBoard.${key}Empty`)}
            </Text>
          )}

          {hidden > 0 && (
            <Button
              size="sm"
              variant="link"
              alignSelf="flex-start"
              onClick={() => setExpanded((e) => ({ ...e, [key]: true }))}
            >
              {t('NextStepsBoard.showMore', { count: hidden })}
            </Button>
          )}
        </Flex>
      </Box>
    )
  }

  return (
    <Box my={5}>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
        {renderColumn('todo', todo)}
        {renderColumn('done', done)}
      </SimpleGrid>

      {!readOnly && (
        <Flex mt={6} gap={3} flexWrap="wrap">
          <Button
            size="sm"
            variant="outline"
            leftIcon={<CreateIcon size={18} />}
            onClick={onAdd}
            isDisabled={!onAdd}
          >
            {t('NextStepsBoard.add')}
          </Button>
          {done.length > 0 && (
            // Not red (D25): archiving is not destructive, and red makes people
            // hesitate exactly where they should not.
            <Button
              size="sm"
              variant="outline"
              leftIcon={<ArchiveIcon size={18} />}
              onClick={handleArchiveDone}
            >
              {t('NextStepsBoard.archiveDone', { count: done.length })}
            </Button>
          )}
        </Flex>
      )}

      {editing && (
        <NextStepModal
          scopeCircleId={circleId}
          nextStep={editing}
          isOpen
          onClose={() => setEditing(undefined)}
        />
      )}
    </Box>
  )
}
